import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { StatusBadge, OverallBadge } from "@/components/StatusBadge";
import UploadDialog from "@/components/UploadDialog";
import ReviewDialog from "@/components/ReviewDialog";
import NMCPanel from "@/components/NMCPanel";
import DBSPanel from "@/components/DBSPanel";
import DocumentLink from "@/components/DocumentLink";
import WorkerAvatar from "@/components/WorkerAvatar";
import PersonalDetailsForm, { EMPTY_PERSONAL_DETAILS } from "@/components/PersonalDetailsForm";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, UploadCloud, FileText, AlertCircle, Clock, CalendarClock, FileDown, Share2, Copy, Link2, X } from "lucide-react";
import { roleLabel, formatDate } from "@/lib/checklist";
import { useAuth } from "@/context/AuthContext";
import api_client from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function WorkerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadItem, setUploadItem] = useState(null);
  const [reviewDoc, setReviewDoc] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/workers/${id}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <AppLayout>
        <div className="text-slate-500">Loading worker…</div>
      </AppLayout>
    );
  }

  const canReview = user?.role === "team_member" || user?.role === "manager";
  const w = data.worker;
  const items = data.items.filter((i) => i.special_type !== "nmc_pin");

  const downloadCqcPack = async () => {
    try {
      const resp = await api_client.get(`/workers/${id}/export-pack.pdf`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = (w.name || "worker").toLowerCase().replace(/[^a-z0-9]+/g, "-");
      a.href = url;
      a.download = `audentra-${safe}-cqc-pack.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not export pack", { description: e.response?.data?.detail || e.message });
    }
  };

  return (
    <AppLayout>
      <button onClick={() => nav(-1)} data-testid="back-btn" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-6">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <WorkerAvatar workerId={w.id} name={w.name} size="lg" bust={data?.summary?.approved} />
          <div>
            <div className="label-caps text-slate-500">Worker profile</div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-1">{w.name}</h1>
            <div className="text-slate-600 mt-1.5">
              {w.email} · <span className="font-semibold text-slate-800">{roleLabel(w.worker_role)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canReview && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShareOpen(true)}
              data-testid="share-pack-btn"
              className="rounded-full h-9 px-4"
            >
              <Share2 className="h-4 w-4 mr-1.5" /> Share with inspector
            </Button>
          )}
          {canReview && (
            <Button
              variant="outline"
              size="sm"
              onClick={downloadCqcPack}
              data-testid="export-cqc-pack"
              className="rounded-full h-9 px-4"
            >
              <FileDown className="h-4 w-4 mr-1.5" /> CQC pack
            </Button>
          )}
          <OverallBadge overall={data.summary.overall} />
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="label-caps text-slate-500">Progress</div>
            <div className="font-heading text-2xl font-bold text-slate-900 mt-1">
              {data.summary.progress}% · {data.summary.approved}/{data.summary.total} approved &amp; in-date
            </div>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {[
              { k: "approved", label: "Approved", color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
              { k: "expiring_soon", label: "Expiring", color: "text-amber-700 bg-amber-50 border-amber-200" },
              { k: "expired", label: "Expired", color: "text-rose-700 bg-rose-50 border-rose-300" },
              { k: "pending", label: "In review", color: "text-amber-700 bg-amber-50 border-amber-200" },
              { k: "rejected", label: "Rejected", color: "text-rose-700 bg-rose-50 border-rose-200" },
              { k: "missing", label: "Missing", color: "text-slate-700 bg-slate-50 border-slate-200" },
            ].map((s) => (
              <div key={s.k} className={`rounded-lg border px-3 py-2 ${s.color}`}>
                <div className="font-heading text-base font-bold tabular-nums">{data.summary[s.k] ?? 0}</div>
                <div className="text-[0.6rem] uppercase tracking-widest">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-5"><Progress value={data.summary.progress} className="h-2" /></div>
      </div>

      {w.worker_role === "registered_nurse" && data.nmc && (
        <div className="mt-6">
          <NMCPanel worker={w} nmc={data.nmc} canReview={canReview} onChanged={load} />
        </div>
      )}

      <PersonalDetailsSection worker={w} onSaved={load} />

      <div className="mt-8 space-y-3">
        {items.map((item) => (
          <div key={item.key} data-testid={`detail-item-${item.key}`} className="rounded-lg border border-slate-200 bg-white p-5 card-lift">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-heading font-semibold text-slate-900">{item.title}</div>
                  <span className="text-[0.65rem] uppercase tracking-widest text-slate-500 font-semibold">
                    {item.category === "standard" ? "Standard" : "Role-specific"}
                  </span>
                  {item.essential === false && (
                    <span className="text-[0.65rem] uppercase tracking-widest text-teal-700 bg-teal-50 border border-teal-100 rounded-full px-2 py-0.5 font-semibold">
                      Desirable
                    </span>
                  )}
                </div>
                <div className="text-sm text-slate-600 mt-1">{item.description}</div>

                {item.document && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                    {item.document.original_filename ? (
                      <>
                        <FileText className="h-3.5 w-3.5" />
                        <span className="truncate">{item.document.original_filename}</span>
                      </>
                    ) : (
                      <span className="italic">Data-only record</span>
                    )}
                    <span>·</span>
                    <Clock className="h-3 w-3" />
                    {new Date(item.document.created_at).toLocaleString()}
                    <span>· by {item.document.uploaded_by_name}</span>
                    {item.document?.metadata?.completed_date && (
                      <>
                        <span>·</span>
                        <span data-testid={`detail-completed-${item.key}`}>
                          completed {formatDate(item.document.metadata.completed_date)}
                        </span>
                      </>
                    )}
                    {item.expiry_date && (
                      <>
                        <span>·</span>
                        <CalendarClock className="h-3 w-3" />
                        <span data-testid={`detail-expiry-${item.key}`}>
                          expires {formatDate(item.expiry_date)}
                        </span>
                        {item.date_field === "completed_date" && (
                          <span className="text-[0.6rem] uppercase tracking-widest text-teal-700 bg-teal-50 border border-teal-100 rounded-full px-1.5 py-0.5 font-semibold">
                            auto
                          </span>
                        )}
                      </>
                    )}
                    {item.document.original_filename && (
                      <DocumentLink
                        docId={item.document.id}
                        testid={`open-file-${item.key}`}
                        className="ml-1"
                      />
                    )}
                  </div>
                )}

                {item.document?.metadata && item.special_type === "right_to_work" && item.document.metadata.citizenship === "non_uk" && (
                  <div className="mt-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                    <div><span className="font-semibold">Share code:</span> {item.document.metadata.share_code}</div>
                    <div><span className="font-semibold">DOB:</span> {item.document.metadata.dob}</div>
                    <div><span className="font-semibold">Visa expiry:</span> {formatDate(item.document.metadata.visa_expiry)}</div>
                  </div>
                )}

                {item.special_type === "dbs" && item.document && (
                  <DBSPanel
                    workerId={w.id}
                    document={item.document}
                    canReview={canReview}
                    onChanged={load}
                  />
                )}

                {item.status === "rejected" && item.document?.rejection_reason && (
                  <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-3 flex gap-2">
                    <AlertCircle className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-rose-800">
                      <div className="font-semibold">Rejected</div>
                      <div className="mt-0.5">{item.document.rejection_reason}</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={item.status} />
                {canReview && item.status === "pending" && item.document && (
                  <Button size="sm" onClick={() => setReviewDoc(item.document)} data-testid={`detail-review-${item.key}`} className="bg-slate-900 hover:bg-slate-800">
                    Review
                  </Button>
                )}
                {canReview && ["missing", "rejected", "expired", "expiring_soon"].includes(item.status) && (
                  <Button size="sm" variant="outline" onClick={() => setUploadItem(item)} data-testid={`detail-upload-${item.key}`}>
                    <UploadCloud className="h-4 w-4 mr-1.5" /> Upload on behalf
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <UploadDialog open={!!uploadItem} onOpenChange={(v) => !v && setUploadItem(null)} item={uploadItem} workerId={id} onUploaded={load} />
      <ReviewDialog open={!!reviewDoc} onOpenChange={(v) => !v && setReviewDoc(null)} document={reviewDoc} onReviewed={load} />
      <SharePackDialog open={shareOpen} onOpenChange={setShareOpen} workerId={id} workerName={w.name} />
    </AppLayout>
  );
}

function PersonalDetailsSection({ worker, onSaved }) {
  const existing = worker.personal_details || null;
  const [values, setValues] = useState({
    ...EMPTY_PERSONAL_DETAILS,
    full_legal_name: existing?.full_legal_name || worker.name || "",
    email: worker.email || "",
    mobile: existing?.mobile || "",
    home_address: existing?.home_address || "",
    date_of_birth: existing?.date_of_birth || "",
    nationality: existing?.nationality || "",
  });

  useEffect(() => {
    setValues({
      ...EMPTY_PERSONAL_DETAILS,
      full_legal_name: existing?.full_legal_name || worker.name || "",
      email: worker.email || "",
      mobile: existing?.mobile || "",
      home_address: existing?.home_address || "",
      date_of_birth: existing?.date_of_birth || "",
      nationality: existing?.nationality || "",
    });
  }, [worker.id, existing?.updated_at]);

  const complete = !!(existing && existing.full_legal_name && existing.mobile && existing.home_address && existing.date_of_birth && existing.nationality);

  return (
    <section
      className="mt-8 rounded-xl border border-slate-200 bg-white p-6"
      data-testid="personal-details-section"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-caps text-slate-500">Personal details</div>
          <h2 className="font-heading text-xl font-bold mt-0.5">Candidate profile</h2>
        </div>
        <span
          data-testid="pd-completeness"
          className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${
            complete
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-amber-50 text-amber-800 border-amber-200"
          }`}
        >
          {complete ? "Complete" : "Needs completion"}
        </span>
      </div>
      <p className="text-sm text-slate-600 mt-1">
        Basic identifying information held against this candidate. All fields
        are required and treated as sensitive personal data.
      </p>
      <div className="mt-6">
        <PersonalDetailsForm
          value={values}
          onChange={setValues}
          accountEmail={worker.email}
          emailEditable={false}
          workerId={worker.id}
          onSaved={onSaved}
        />
      </div>
    </section>
  );
}

function SharePackDialog({ open, onOpenChange, workerId, workerName }) {
  const [ttl, setTtl] = useState("168");
  const [maxUses, setMaxUses] = useState("");
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [links, setLinks] = useState([]);

  const load = useCallback(async () => {
    if (!open) return;
    try {
      const { data } = await api_client.get(`/workers/${workerId}/share-pack`);
      setLinks(data);
    } catch (_e) {
      /* ignore */
    }
  }, [open, workerId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      const body = { ttl_hours: Number(ttl) };
      if (maxUses && Number(maxUses) > 0) body.max_uses = Number(maxUses);
      if (note.trim()) body.note = note.trim();
      const { data } = await api_client.post(`/workers/${workerId}/share-pack`, body);
      toast.success("Share link created", { description: "Anyone with this link can view the pack until it expires." });
      try {
        await navigator.clipboard.writeText(data.url);
        toast.success("Link copied to clipboard");
      } catch (_e) {
        /* ignore clipboard errors */
      }
      setMaxUses("");
      setNote("");
      load();
    } catch (e) {
      toast.error("Could not create link", { description: e.response?.data?.detail || e.message });
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (token) => {
    try {
      await api_client.post(`/share-pack/${token}/revoke`);
      toast.success("Link revoked");
      load();
    } catch (e) {
      toast.error("Could not revoke", { description: e.response?.data?.detail || e.message });
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch (_e) {
      toast.error("Could not copy — long-press to copy manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-2xl" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Share {workerName}&apos;s CQC pack</DialogTitle>
          <DialogDescription>
            Generate a time-limited link an inspector can open without an Audentra account.
            Each view is audit-logged; revoke at any time.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="label-caps">Expires after</Label>
            <Select value={ttl} onValueChange={setTtl}>
              <SelectTrigger data-testid="share-ttl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 hour</SelectItem>
                <SelectItem value="24">1 day</SelectItem>
                <SelectItem value="72">3 days</SelectItem>
                <SelectItem value="168">7 days</SelectItem>
                <SelectItem value="336">14 days</SelectItem>
                <SelectItem value="720">30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="label-caps">Max views (optional)</Label>
            <Input
              type="number"
              min={1}
              max={100}
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              placeholder="Unlimited"
              data-testid="share-max-uses"
            />
          </div>
        </div>

        <div className="space-y-1.5 mt-3">
          <Label className="label-caps">Note for the inspector (optional)</Label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="share-note"
            placeholder="e.g. Compliance pack for Sarah Patel, prepared for the Q1 inspection. Any questions, ping dave@…"
            maxLength={500}
            rows={3}
            className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-1"
          />
          <p className="text-xs text-slate-500">Shows on the inspector&apos;s landing page above the download button ({note.length}/500).</p>
        </div>

        <Button
          onClick={create}
          disabled={creating}
          className="w-full mt-2 rounded-full bg-teal-700 hover:bg-teal-800"
          data-testid="share-create"
        >
          <Link2 className="h-4 w-4 mr-1.5" /> {creating ? "Creating…" : "Create share link"}
        </Button>

        <div className="mt-4 space-y-2 max-h-80 overflow-y-auto" data-testid="share-list">
          {links.length === 0 && (
            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
              No share links yet.
            </div>
          )}
          {links.map((l) => (
            <div
              key={l.token}
              data-testid={`share-item-${l.token.slice(0, 8)}`}
              className={`rounded-md border p-3 text-sm ${l.active ? "border-teal-200 bg-teal-50" : "border-slate-200 bg-slate-50"}`}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs text-slate-800 truncate">{l.url}</div>
                  <div className="text-[0.7rem] text-slate-500 mt-1">
                    {l.active ? "Active" : l.revoked ? "Revoked" : "Expired"} · expires {new Date(l.expires_at).toLocaleString()} · {l.uses}{l.max_uses ? `/${l.max_uses}` : ""} views · by {l.created_by_name}
                  </div>
                  {l.note && (
                    <div className="mt-1.5 text-xs text-slate-700 border-l-2 border-teal-300 pl-2 italic">
                      &ldquo;{l.note}&rdquo;
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" className="h-8" onClick={() => copy(l.url)} data-testid={`share-copy-${l.token.slice(0, 8)}`}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  {l.active && (
                    <Button size="sm" variant="outline" className="h-8 text-rose-600 hover:text-rose-700" onClick={() => revoke(l.token)} data-testid={`share-revoke-${l.token.slice(0, 8)}`}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
